<template>
  <StatsWidget
    :title="$t('stats_dashboard.margin_under_threshold.title')"
    :description="$t('stats_dashboard.margin_under_threshold.description')"
    icon="exclamation-triangle"
    :tone="headerTone"
    :loading="widget.loading"
    :error="widget.error"
    :has-data="hasData"
    :span-full="true"
    :empty-icon="'exclamation-triangle'"
    :empty-message="$t('stats_dashboard.margin_under_threshold.empty')"
    @refresh="$emit('refresh')"
  >
    <div class="stats-margin-under-threshold">
      <!-- Totals strip — flagged count + estimated margin loss -->
      <div class="stats-margin-under-threshold__totals">
        <div
          class="stats-margin-under-threshold__total-card"
          :class="{
            'stats-margin-under-threshold__total-card--danger': flaggedCount > 0,
            'stats-margin-under-threshold__total-card--success': flaggedCount === 0,
          }"
        >
          <span class="stats-margin-under-threshold__total-label">
            {{ $t('stats_dashboard.margin_under_threshold.totals.flagged_count') }}
          </span>
          <span class="stats-margin-under-threshold__total-value">
            {{ formatInteger(flaggedCount) }}
          </span>
        </div>
        <div class="stats-margin-under-threshold__total-card">
          <span class="stats-margin-under-threshold__total-label">
            {{ $t('stats_dashboard.margin_under_threshold.totals.flagged_value') }}
          </span>
          <span class="stats-margin-under-threshold__total-value">
            {{ formatCurrency(totals.flagged_value) }}
          </span>
        </div>
        <div
          class="stats-margin-under-threshold__total-card stats-margin-under-threshold__total-card--loss"
        >
          <span class="stats-margin-under-threshold__total-label">
            {{ $t('stats_dashboard.margin_under_threshold.totals.flagged_margin_loss') }}
          </span>
          <span class="stats-margin-under-threshold__total-value">
            {{ formatCurrency(totals.flagged_margin_loss) }}
          </span>
        </div>
      </div>

      <!-- Celebratory "all clear" state when the list is empty.
           Kept inside the content slot rather than relying on the
           generic widget empty state, so we can use a green tone
           instead of the neutral skeleton-grey one. -->
      <div v-if="items.length === 0" class="stats-margin-under-threshold__all-clear">
        <div class="stats-margin-under-threshold__all-clear-icon">
          <BaseIcon name="check-circle" size="22" />
        </div>
        <h4 class="stats-margin-under-threshold__all-clear-title">
          {{ $t('stats_dashboard.margin_under_threshold.all_clear_title') }}
        </h4>
        <p class="stats-margin-under-threshold__all-clear-description">
          {{ $t('stats_dashboard.margin_under_threshold.all_clear_description') }}
        </p>
      </div>

      <!-- Flagged-lines table -->
      <div
        v-else
        class="stats-margin-under-threshold__table-wrap"
        role="region"
        :aria-label="$t('stats_dashboard.margin_under_threshold.title')"
      >
        <table class="stats-margin-under-threshold__table">
          <thead>
            <tr>
              <th scope="col" class="stats-margin-under-threshold__th-date">
                {{ $t('stats_dashboard.margin_under_threshold.headers.date') }}
              </th>
              <th scope="col">
                {{ $t('stats_dashboard.margin_under_threshold.headers.product') }}
              </th>
              <th scope="col" class="stats-margin-under-threshold__th-num">
                {{ $t('stats_dashboard.margin_under_threshold.headers.applied') }}
              </th>
              <th scope="col" class="stats-margin-under-threshold__th-num">
                {{ $t('stats_dashboard.margin_under_threshold.headers.min') }}
              </th>
              <th scope="col" class="stats-margin-under-threshold__th-num">
                {{ $t('stats_dashboard.margin_under_threshold.headers.gap') }}
              </th>
              <th scope="col" class="stats-margin-under-threshold__th-num">
                {{ $t('stats_dashboard.margin_under_threshold.headers.amount') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, idx) in items"
              :key="rowKey(item, idx)"
              class="stats-margin-under-threshold__row"
            >
              <td class="stats-margin-under-threshold__cell-date">
                {{ formatShortDate(item.accepted_at) }}
              </td>
              <td class="stats-margin-under-threshold__cell-product">
                <div class="stats-margin-under-threshold__product">
                  <span class="stats-margin-under-threshold__product-name">
                    {{
                      item.product_title ||
                      $t('stats_dashboard.margin_under_threshold.unknown_product')
                    }}
                  </span>
                  <span v-if="item.description" class="stats-margin-under-threshold__product-desc">
                    {{ item.description }}
                  </span>
                </div>
              </td>
              <td class="stats-margin-under-threshold__cell-num">
                <span class="stats-margin-under-threshold__applied">
                  {{ formatPercent(item.applied_margin_pct) }}
                </span>
              </td>
              <td class="stats-margin-under-threshold__cell-num">
                {{ formatPercent(item.min_margin_pct) }}
              </td>
              <td class="stats-margin-under-threshold__cell-num">
                <span class="stats-margin-under-threshold__gap" :class="gapClass(item.gap_pct)">
                  {{ formatGapLabel(item.gap_pct) }}
                </span>
              </td>
              <td class="stats-margin-under-threshold__cell-num">
                <div class="stats-margin-under-threshold__amount">
                  <span class="stats-margin-under-threshold__amount-value">
                    {{ formatCurrency(item.revenue) }}
                  </span>
                  <span class="stats-margin-under-threshold__amount-qty">
                    {{
                      $t('stats_dashboard.margin_under_threshold.qty_x', {
                        n: formatInteger(item.quantity),
                      })
                    }}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </StatsWidget>
</template>

<script>
  /**
   * Under-threshold alert table (C in the 2026-05-12 margin delta).
   *
   * Lists individual proposal items where the realised margin sits
   * below the product's configured `min_margin_pct`. Backend already
   * sorts worst-first by `gap_pct` and skips items without a
   * configured threshold, so we render the response as-is.
   *
   * UX choices:
   *   • Widget tone shifts to rose/red when there are flagged items
   *     so the page-level scan immediately surfaces the alert.
   *   • An empty list is *good news* — we replace the generic empty
   *     state with a celebratory green "Geen offertes onder de
   *     minimum marge" block.
   *   • The gap column shows the gap as a negative percent-points
   *     value to match the visual language of the totals widget.
   */
  import { computed, getCurrentInstance } from 'vue';
  import StatsWidget from './StatsWidget.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import {
    formatCurrency,
    formatInteger,
    formatPercent,
    formatPercentPointsDelta,
    formatShortDate,
  } from '@/utils/statsFormat';

  export default {
    name: 'StatsMarginUnderThreshold',

    components: { StatsWidget, BaseIcon },

    props: {
      widget: { type: Object, required: true },
    },

    emits: ['refresh'],

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const items = computed(() => props.widget.data?.items || []);
      const totals = computed(() => props.widget.data?.totals || {});

      const flaggedCount = computed(() => Number(totals.value.flagged_count) || 0);

      // We always render the widget body when we have *any* data —
      // even an empty list, because an empty list is good news that
      // we want to display, not skip with an empty state.
      const hasData = computed(() => props.widget.data != null);

      // Header tone follows the alert level. Rose feels appropriate
      // when there's stuff on fire; green when everything's healthy;
      // slate when we haven't loaded anything yet.
      const headerTone = computed(() => {
        if (!props.widget.data) return 'slate';
        return flaggedCount.value > 0 ? 'rose' : 'green';
      });

      const gapClass = (gap) => {
        const value = Math.abs(Number(gap) || 0);
        if (value >= 10) return 'stats-margin-under-threshold__gap--severe';
        if (value >= 5) return 'stats-margin-under-threshold__gap--moderate';
        return 'stats-margin-under-threshold__gap--mild';
      };

      // Render gap as a signed delta so the user immediately reads
      // it as "below the floor". Backend returns gap as a positive
      // number (min − applied); we negate for display only.
      const formatGapLabel = (gap) => {
        const value = Math.abs(Number(gap) || 0);
        return formatPercentPointsDelta(-value);
      };

      const rowKey = (item, idx) =>
        item.item_id || `${item.proposal_id || 'unknown'}-${item.product_id || 'np'}-${idx}`;

      return {
        items,
        totals,
        flaggedCount,
        hasData,
        headerTone,
        gapClass,
        formatGapLabel,
        rowKey,
        formatCurrency,
        formatInteger,
        formatPercent,
        formatShortDate,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-margin-under-threshold {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex: 1;
    min-height: 0;

    // ---------- Totals strip ----------

    &__totals {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 0.625rem;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__total-card {
      padding: 0.625rem 0.875rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-100);
      border-radius: 0.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;

      &--danger {
        background: var(--rose-50, #fff1f2);
        border-color: rgba(244, 63, 94, 0.25);

        .stats-margin-under-threshold__total-value {
          color: var(--rose-700, #be123c);
        }
      }

      &--success {
        background: var(--green-50, #ecfdf5);
        border-color: rgba(16, 185, 129, 0.25);

        .stats-margin-under-threshold__total-value {
          color: var(--green-700, #047857);
        }
      }

      &--loss {
        background: linear-gradient(180deg, #fff7ed, #f8fafc);
        border-color: #fed7aa;
      }
    }

    &__total-label {
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      @include ellipsis(1);
    }

    &__total-value {
      font-size: 1.125rem;
      font-weight: 700;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
      @include ellipsis(1);
    }

    // ---------- All-clear state ----------

    &__all-clear {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.625rem;
      padding: 2rem 1rem;
      background: linear-gradient(180deg, #ecfdf5, #f8fafc);
      border: 1px solid #bbf7d0;
      border-radius: 0.75rem;
      text-align: center;
    }

    &__all-clear-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: rgba(16, 185, 129, 0.12);
      color: var(--green-600);
    }

    &__all-clear-title {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 700;
      color: var(--green-700, #047857);
    }

    &__all-clear-description {
      margin: 0;
      max-width: 32rem;
      font-size: 0.8125rem;
      color: var(--slate-600);
      line-height: 1.5;
    }

    // ---------- Table ----------

    &__table-wrap {
      overflow-x: auto;
      border: 1px solid var(--slate-100);
      border-radius: 0.625rem;
    }

    &__table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.8125rem;
      color: var(--slate-700);

      th,
      td {
        padding: 0.625rem 0.75rem;
        text-align: left;
        border-bottom: 1px solid var(--slate-100);
        vertical-align: top;
      }

      thead th {
        background: var(--slate-50);
        font-size: 0.6875rem;
        font-weight: 600;
        color: var(--slate-500);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        position: sticky;
        top: 0;
      }

      tbody tr:last-child td {
        border-bottom: 0;
      }
    }

    &__th-num,
    &__cell-num {
      text-align: right;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }

    &__th-date {
      white-space: nowrap;
    }

    &__row {
      transition: background-color 0.15s ease;

      @include hover {
        background: var(--slate-50);
      }
    }

    &__cell-date {
      font-size: 0.75rem;
      color: var(--slate-500);
      white-space: nowrap;
      padding-top: 0.75rem;
    }

    &__cell-product {
      min-width: 16rem;
    }

    &__product {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;
    }

    &__product-name {
      font-weight: 600;
      color: var(--slate-900);
      @include ellipsis(1);
    }

    &__product-desc {
      font-size: 0.75rem;
      color: var(--slate-500);
      @include ellipsis(1);
    }

    &__applied {
      font-weight: 700;
      color: var(--rose-700, #be123c);
    }

    &__gap {
      display: inline-flex;
      align-items: center;
      padding: 0.0625rem 0.4375rem;
      border-radius: 9999px;
      font-weight: 600;
      font-size: 0.75rem;

      &--severe {
        background: rgba(244, 63, 94, 0.12);
        color: var(--rose-700, #be123c);
      }

      &--moderate {
        background: var(--amber-50, #fffbeb);
        color: var(--amber-700, #b45309);
      }

      &--mild {
        background: var(--slate-100);
        color: var(--slate-700);
      }
    }

    &__amount {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.0625rem;
    }

    &__amount-value {
      font-weight: 700;
      color: var(--slate-900);
    }

    &__amount-qty {
      font-size: 0.6875rem;
      color: var(--slate-500);
    }
  }
</style>
